import React, { useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterMultiSelect from './FilterMultiSelect';
import FilterRangeBands from './FilterRangeBands';
import FilterRangeMatrix from './FilterRangeMatrix';
import FilterSegmentedControl from './FilterSegmentedControl';
import FilterInlineCheckbox from './FilterInlineCheckbox';
import FilterTextInput from './FilterTextInput';
import FilterRangeInput from './FilterRangeInput';
import FilterAccordion from './FilterAccordion';
import FilterAppliedChips from './FilterAppliedChips';
import FilterScopeSelect from './FilterScopeSelect';
import FilterPanelTabs from './FilterPanelTabs';
import OfficialUnitFilterStatus from './OfficialUnitFilterStatus';
import {
  filterSectionLabelStyle,
  filterPanelStyle,
  filterPanelSidebarPositionStyle,
  filterPanelBodyScrollStyle,
  filterPanelBodyScrollClassName,
  filterPanelHeaderStyle,
  filterPanelHeaderRowStyle,
  filterPanelTitleStyle,
  filterPanelTitleDividerStyle,
  filterAppliedChipsStickyStyle,
  filterStickyTopStyle,
  filterScopeStickyStyle,
  filterTabsStickyStyle,
  filterPanelFooterStyle,
  filterPanelFooterActionsRowStyle,
  filterClearLinkStyle,
  filterApplyButtonClassName,
  filterApplyButtonStyle,
  filterDisabledMessageStyle,
  filterCoverageNoteStyle,
  filterShowOnlyRowStyle,
  filterShowOnlyTextBlockStyle,
  filterShowOnlyTitleStyle,
  filterShowOnlyDescriptionStyle,
  filterShowOnlyToggleStyle,
  filterShowOnlyToggleKnobStyle,
  filterPanelKeyframes,
  filterAccentCssVars,
  filterPanelGutterStyle,
} from './FilterPanel.styles';
import {
  getFilterPanelGroups,
  getAppliedFilterChips,
  countActiveFiltersInGroup,
  countActiveFiltersInGroups,
  FILTER_PANEL_TAB,
} from '../../data/parcelFilterContract';
import { panelCloseIconButtonSx } from '../PanelShell/PanelHeader';

/**
 * Panel de filtros de parcelas — organizado por dominio conceptual.
 */
const FilterPanel = ({
  activeFilters,
  activeCount,
  showOnlyFiltered,
  onSetFilterValue,
  onRemoveFilterChip,
  onToggleShowOnlyFiltered,
  onClearAll,
  onClose,
  onOpenResultsList = null,
  disabled = false,
  disabledMessage = 'Inicia sesión para aplicar filtros',
  isMobile = false,
  anchorRef = null,
  scope = null,
  scopeLists = [],
  scopeListsLoading = false,
  scopeLoading = false,
  scopeError = null,
  scopeCount = 0,
  scopeMatchCount = null,
  onSetScope = null,
  onScopeDropdownOpen = null,
  scopeLocked = false,
  scopeLockedHint = null,
  mapOptionsByField = null,
  onRequestMapOptions = null,
  bottomOffset = 0,
  accentColor = null,
  showListingFilters = false,
  showOfficialUnitFilters = false,
  showOfficialUnitCoverageNote = false,
  officialUnitFilter = null,
  officialUnitZoomLocked = false,
  focusGroupId = null,
  filterGroups = null,
  hideTabs = false,
  hideShowOnlyFiltered = false,
  resolveAppliedChips = null,
}) => {
  const panelRef = useRef(null);
  const [anchorLeft, setAnchorLeft] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const scopeEnabled = typeof onSetScope === 'function';
  const isScopeActive = Boolean(scope?.type && scope.type !== 'all');

  const groupOptions = useMemo(() => ({
    includeListing: showListingFilters,
    hideScopeUnsupported: showListingFilters || isScopeActive,
    includeOfficialUnits: showOfficialUnitFilters,
  }), [showListingFilters, isScopeActive, showOfficialUnitFilters]);

  const lotGroups = useMemo(
    () => getFilterPanelGroups({ ...groupOptions, tab: FILTER_PANEL_TAB.lot }),
    [groupOptions],
  );
  const unitsGroups = useMemo(
    () => getFilterPanelGroups({ ...groupOptions, tab: FILTER_PANEL_TAB.units }),
    [groupOptions],
  );
  const lotFilterCount = useMemo(
    () => countActiveFiltersInGroups(lotGroups, activeFilters),
    [lotGroups, activeFilters],
  );
  const unitsFilterCount = useMemo(
    () => countActiveFiltersInGroups(unitsGroups, activeFilters),
    [unitsGroups, activeFilters],
  );
  // El panel se desmonta al cerrar: al reabrir, Unidades solo si es el único
  // tab con filtros activos. Con ambos (o solo Lote) queda en Lote.
  const [activeTab, setActiveTab] = useState(
    unitsFilterCount > 0 && lotFilterCount === 0
      ? FILTER_PANEL_TAB.units
      : FILTER_PANEL_TAB.lot,
  );
  const useCustomGroups = Array.isArray(filterGroups) && filterGroups.length > 0;
  const groups = useCustomGroups
    ? filterGroups
    : (activeTab === FILTER_PANEL_TAB.units ? unitsGroups : lotGroups);
  const showTabs = !hideTabs && !useCustomGroups;
  const appliedChips = useMemo(
    () => (typeof resolveAppliedChips === 'function'
      ? resolveAppliedChips(activeFilters, mapOptionsByField)
      : getAppliedFilterChips(activeFilters, mapOptionsByField)),
    [activeFilters, mapOptionsByField, resolveAppliedChips],
  );

  // El alcance cuenta como acción salvo cuando está bloqueado por un panel.
  const hasAnythingActive = activeCount > 0 || (isScopeActive && !scopeLocked);

  const updateAnchorPosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor || isMobile) {
      setAnchorLeft(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setAnchorLeft(Math.round(rect.left));
  }, [anchorRef, isMobile]);

  useLayoutEffect(() => {
    updateAnchorPosition();
    window.addEventListener('resize', updateAnchorPosition);
    window.addEventListener('scroll', updateAnchorPosition, true);
    return () => {
      window.removeEventListener('resize', updateAnchorPosition);
      window.removeEventListener('scroll', updateAnchorPosition, true);
    };
  }, [updateAnchorPosition]);

  useLayoutEffect(() => {
    if (!openDropdownId) return undefined;
    const handlePointerDown = (event) => {
      const dropdownEl = event.target.closest('[data-filter-dropdown]');
      if (dropdownEl?.dataset.filterDropdown === openDropdownId) return;
      setOpenDropdownId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openDropdownId]);

  useLayoutEffect(() => {
    if (!focusGroupId || !panelRef.current) return undefined;
    const node = panelRef.current.querySelector(`[data-filter-group="${focusGroupId}"]`);
    node?.scrollIntoView({ block: 'nearest' });
    return undefined;
  }, [focusGroupId]);

  const renderFilter = (def) => {
    const value = activeFilters[def.field];
    const isDisabled = disabled;
    const officialUnitBandsLocked = officialUnitZoomLocked
      && def.source === 'official_unit'
      && def.component !== 'textContains';

    switch (def.component) {
      case 'multiSelect': {
        // El contrato no puede tener un catálogo de barrios estático: las
        // opciones (y su leyenda) las provee quien conoce el origen vigente.
        const fromMap = def.optionsFrom === 'map';
        const dynamic = fromMap ? mapOptionsByField?.[def.field] : null;
        const options = fromMap ? (dynamic?.options || []) : def.options;
        return (
          <FilterMultiSelect
            key={def.id}
            filterId={def.id}
            label={def.label}
            options={options}
            selectedValues={Array.isArray(value) ? value : []}
            searchable={def.searchable}
            emptyHint={dynamic?.emptyHint || def.emptyHint}
            hint={dynamic?.hint || def.hint}
            isOpen={openDropdownId === def.id}
            onToggle={() => {
              const willOpen = openDropdownId !== def.id;
              setOpenDropdownId(willOpen ? def.id : null);
              if (willOpen && fromMap) onRequestMapOptions?.(def.field);
            }}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
            disabled={isDisabled}
          />
        );
      }
      case 'rangeBands': {
        // Las bandas de precio dependen de los datos de la lista, no del contrato.
        const dynamic = def.optionsFrom === 'map' ? mapOptionsByField?.[def.field] : null;
        const options = def.optionsFrom === 'map' ? (dynamic?.options || []) : def.options;
        const isEmpty = options.length === 0;
        // Sin bandas y sin nada accionable que decir, el filtro no se muestra.
        if (isEmpty && def.optionsFrom && !dynamic?.emptyHint) return null;
        return (
          <FilterRangeBands
            key={def.id}
            label={def.label}
            options={options}
            selectedValues={Array.isArray(value) ? value : []}
            disabled={isDisabled || officialUnitBandsLocked || isEmpty}
            columns={def.columns}
            hint={(isEmpty ? dynamic?.emptyHint : (dynamic?.hint || def.hint)) || null}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
          />
        );
      }
      case 'rangeMatrix':
        return (
          <FilterRangeMatrix
            key={def.id}
            bandLabels={def.bandLabels}
            unit={def.unit}
            rows={def.rows}
            valuesByField={Object.fromEntries(
              (def.rows || []).map((row) => [row.field, activeFilters[row.field]]),
            )}
            disabled={isDisabled}
            onChangeRow={(field, next) => onSetFilterValue?.(field, next)}
          />
        );
      case 'triState':
        return (
          <FilterSegmentedControl
            key={def.id}
            options={def.options}
            value={value}
            disabled={isDisabled}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
          />
        );
      case 'inlineBoolean':
        return (
          <FilterInlineCheckbox
            key={def.id}
            label={def.label}
            checked={value === def.trueValue}
            disabled={isDisabled}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
          />
        );
      case 'textContains':
        return (
          <FilterTextInput
            key={def.id}
            label={def.label}
            value={typeof value === 'string' ? value : null}
            placeholder={def.placeholder}
            minLength={def.minLength}
            disabled={isDisabled}
            hint={def.hint}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
          />
        );
      case 'range':
        return (
          <FilterRangeInput
            key={def.id}
            label={def.label}
            value={value && typeof value === 'object' ? value : null}
            disabled={isDisabled}
            hint={def.hint}
            onChange={(next) => onSetFilterValue?.(def.field, next)}
          />
        );
      default:
        return null;
    }
  };

  if (!isMobile && anchorRef && anchorLeft == null) return null;

  return (
    <>
      <style>{filterPanelKeyframes}</style>
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Filtrar lotes"
        style={{
          ...filterPanelStyle,
          ...filterAccentCssVars(accentColor),
          ...filterPanelSidebarPositionStyle({ isMobile, anchorLeft, bottomOffset }),
        }}
      >
        <div style={filterPanelHeaderStyle}>
          <div style={filterPanelHeaderRowStyle}>
            <h2 style={filterPanelTitleStyle}>Filtrar</h2>
            <IconButton
              onClick={() => onClose?.()}
              size="small"
              sx={panelCloseIconButtonSx}
              aria-label="Cerrar"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div style={filterPanelTitleDividerStyle} aria-hidden />
        </div>

        <div style={filterStickyTopStyle}>
          {!disabled && (
            <div style={filterAppliedChipsStickyStyle}>
              <FilterAppliedChips
                chips={appliedChips}
                onRemove={(chip) => onRemoveFilterChip?.(chip)}
              />
            </div>
          )}

          {scopeEnabled && (
            <div style={filterScopeStickyStyle}>
              <div style={{ ...filterSectionLabelStyle, marginTop: '4px' }}>
                Aplicar sobre
              </div>
              <FilterScopeSelect
                scope={scope}
                lists={scopeLists}
                listsLoading={scopeListsLoading}
                scopeLoading={scopeLoading}
                scopeError={scopeError}
                scopeCount={scopeCount}
                scopeMatchCount={scopeMatchCount}
                hasActiveFilters={activeCount > 0}
                isOpen={!scopeLocked && openDropdownId === 'scope-select'}
                onToggle={() => {
                  if (scopeLocked) return;
                  setOpenDropdownId((prev) => {
                    const next = prev === 'scope-select' ? null : 'scope-select';
                    if (next) onScopeDropdownOpen?.();
                    return next;
                  });
                }}
                onSelect={(nextScope) => onSetScope(nextScope)}
                disabled={disabled}
                locked={scopeLocked}
                lockedHint={scopeLockedHint}
              />
            </div>
          )}

          {showTabs ? (
            <div style={filterTabsStickyStyle}>
              <FilterPanelTabs
                value={activeTab}
                lotCount={lotFilterCount}
                unitsCount={unitsFilterCount}
                onChange={(nextTab) => {
                  setOpenDropdownId(null);
                  setActiveTab(nextTab);
                }}
              />
            </div>
          ) : null}
        </div>

        <div className={filterPanelBodyScrollClassName} style={filterPanelBodyScrollStyle}>
          {activeTab === FILTER_PANEL_TAB.units && showOfficialUnitCoverageNote && (
            <div style={{ ...filterCoverageNoteStyle, ...filterPanelGutterStyle, marginTop: '12px' }}>
              Las UF catastrales se pueden filtrar en Córdoba. Seleccioná esa
              provincia en el mapa para ver esos criterios.
            </div>
          )}
          {groups.length === 0 && activeTab === FILTER_PANEL_TAB.units && (
            <div style={{ ...filterCoverageNoteStyle, ...filterPanelGutterStyle, marginTop: '12px' }}>
              {showListingFilters
                ? 'No hay filtros de unidades en este alcance.'
                : 'Para filtrar publicaciones (precio, ambientes, operación), aplicá el alcance sobre tus registros o una lista.'}
            </div>
          )}
          {groups.map((group, index) => (
            <React.Fragment key={group.id}>
              <FilterAccordion
                groupId={group.id}
                label={group.label}
                defaultOpen={
                  focusGroupId
                    ? (group.id === focusGroupId || group.accordion === false)
                    : group.defaultOpen
                }
                accordion={group.accordion}
                showDivider={index > 0}
                activeCount={countActiveFiltersInGroup(group, activeFilters)}
              >
                {group.filters.map(renderFilter)}
              </FilterAccordion>
              {group.id === 'contexto' && (
                <div style={{ ...filterCoverageNoteStyle, ...filterPanelGutterStyle }}>
                  Algunos filtros pueden no tener datos todavía en ciertas zonas del país.
                </div>
              )}
              {group.id === 'uf_catastral' && (
                <div style={{ ...filterPanelGutterStyle, marginBottom: '4px' }}>
                  <OfficialUnitFilterStatus
                    status={officialUnitFilter?.status}
                    matchCount={officialUnitFilter?.matchCount}
                    unitCount={officialUnitFilter?.unitCount}
                    zoomLocked={officialUnitZoomLocked}
                    onRetry={disabled ? undefined : officialUnitFilter?.retry}
                  />
                </div>
              )}
            </React.Fragment>
          ))}

          {hideShowOnlyFiltered ? null : (
            <div style={filterShowOnlyRowStyle}>
              <div style={filterShowOnlyTextBlockStyle}>
                <span style={filterShowOnlyTitleStyle(disabled || !hasAnythingActive)}>
                  Mostrar solo filtradas
                </span>
                <span style={filterShowOnlyDescriptionStyle(disabled || !hasAnythingActive)}>
                  Oculta lotes que no coinciden
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenDropdownId(null);
                  onToggleShowOnlyFiltered?.();
                }}
                disabled={disabled || !hasAnythingActive}
                style={filterShowOnlyToggleStyle({
                  active: showOnlyFiltered,
                  disabled: disabled || !hasAnythingActive,
                })}
                aria-pressed={showOnlyFiltered}
                aria-label="Mostrar solo lotes filtrados"
              >
                <span style={filterShowOnlyToggleKnobStyle({
                  active: showOnlyFiltered,
                  disabled: disabled || !hasAnythingActive,
                })}
                />
              </button>
            </div>
          )}
        </div>

        <div style={filterPanelFooterStyle}>
          <div style={filterPanelFooterActionsRowStyle}>
            <button
              type="button"
              style={{
                ...filterClearLinkStyle,
                opacity: disabled || !hasAnythingActive ? 0.45 : 1,
                cursor: disabled || !hasAnythingActive ? 'default' : 'pointer',
              }}
              onClick={disabled || !hasAnythingActive ? undefined : () => {
                onClearAll?.();
                setOpenDropdownId(null);
              }}
              disabled={disabled || !hasAnythingActive}
            >
              Limpiar
            </button>
            <button
              type="button"
              className={filterApplyButtonClassName}
              style={filterApplyButtonStyle}
              onClick={!disabled && hasAnythingActive ? () => {
                setOpenDropdownId(null);
                if (typeof onOpenResultsList === 'function' && activeCount > 0) {
                  onOpenResultsList();
                }
                onClose?.();
              } : undefined}
              disabled={disabled || !hasAnythingActive}
            >
              Ver resultados
            </button>
          </div>

          {disabled && (
            <div style={filterDisabledMessageStyle}>
              {disabledMessage}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
